import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import LangSwitch from "./LangSwitch";
import { useI18n } from "../i18n";
import { useSiteData } from "../siteData";
import { BRAND_L } from "../content";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openSub, setOpenSub] = useState("");
  const { pathname } = useLocation();
  const { t, loc } = useI18n();
  const { content, collections } = useSiteData();

  const mainCategories = collections.filter((c) => !c.parent);

  const nav = (content.menu?.items || [])
    .filter((i) => i && i.visible !== false)
    .map((item) => {
      const manual = (item.children || []).filter((c) => c && c.visible !== false);
      const auto =
        item.auto === "collections"
          ? mainCategories
              .filter((c) => (c.menu_parent || "/products") === item.path)
              .map((c) => ({ label: { en: c.name }, path: `/products/${c.key}` }))
          : [];
      return { ...item, kids: [...manual, ...auto] };
    });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setOpenSub("");
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const navId = (path) => (path === "/" ? "home" : path.replace(/^\//, "").replace(/\//g, "-"));

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 border-b transition-[background-color,box-shadow,border-color] duration-400 ${
          scrolled
            ? "bg-[#FAFAFA]/95 backdrop-blur-md border-stone-200 shadow-[0_1px_20px_rgba(20,20,20,0.05)]"
            : "bg-[#FAFAFA]/60 backdrop-blur-md border-transparent"
        }`}
      >
        <div className="shell flex items-center justify-between h-[74px] lg:h-[90px] gap-6">
          <Link to="/" className="flex items-baseline gap-2.5 group shrink-0">
            <span className="display text-[26px] lg:text-[32px] text-[#141414] leading-none">HRD</span>
            <span lang="en" className="mono text-[9px] text-[#6B675F] hidden xl:block pb-[3px]">
              Architecture
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {nav.map((item) => (
              <div
                key={item.path}
                className="relative"
                onMouseEnter={() => item.kids.length && setOpenSub(item.path)}
                onMouseLeave={() => setOpenSub("")}
              >
                <Link
                  to={item.path}
                  data-testid={`nav-${navId(item.path)}`}
                  className={`mono text-[16px] leading-none text-[#141414] link-underline inline-flex items-center gap-1.5 transition-opacity duration-300 hover:opacity-100 ${
                    pathname === item.path ? "active opacity-100" : "opacity-75"
                  }`}
                >
                  {loc(item.label)}
                  {item.kids.length > 0 && <ChevronDown size={12} strokeWidth={1.8} />}
                </Link>

                {item.kids.length > 0 && (
                  <div
                    data-testid={`nav-dropdown-${navId(item.path)}`}
                    className={`absolute left-0 top-full pt-4 min-w-[260px] transition-all duration-300 ${
                      openSub === item.path
                        ? "opacity-100 translate-y-0 pointer-events-auto"
                        : "opacity-0 -translate-y-1 pointer-events-none"
                    }`}
                  >
                    <div className="bg-[#FAFAFA] border border-[#E2E0D8] shadow-[0_18px_44px_rgba(20,20,20,0.09)] py-2">
                      {item.kids.map((kid) => (
                        <Link
                          key={kid.path}
                          to={kid.path}
                          data-testid={`nav-sub-${navId(kid.path)}`}
                          className="block px-5 py-3 text-[14px] text-[#141414]/80 hover:text-[#141414] hover:bg-[#F2F0EB] transition-colors duration-200"
                        >
                          {loc(kid.label)}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            <LangSwitch className="hidden sm:inline-flex" />
            <Link
              to="/contact"
              className="hidden xl:inline-flex items-center gap-2 border border-[#141414] px-5 py-2.5 mono text-[10px] text-[#141414] hover:bg-[#141414] hover:text-[#F7F6F2] transition-colors duration-300"
            >
              {loc(content.menu?.ctaLabel) || t("cta.quote")} <ArrowUpRight size={13} strokeWidth={1.6} />
            </Link>
            <button
              aria-label="Menu"
              data-testid="menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden w-10 h-10 -mr-2 flex items-center justify-center text-[#141414]"
            >
              {open ? <X size={22} strokeWidth={1.4} /> : <Menu size={22} strokeWidth={1.4} />}
            </button>
          </div>
        </div>
      </header>

      <div
        onClick={() => setOpen(false)}
        className={`fixed inset-0 z-40 bg-[#141414]/25 backdrop-blur-[2px] lg:hidden transition-opacity duration-400 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />
      <aside
        className={`fixed top-0 right-0 bottom-0 z-50 w-[86%] max-w-[400px] bg-[#FAFAFA] border-l border-[#E2E0D8] lg:hidden flex flex-col shadow-[-14px_0_44px_rgba(20,20,20,0.08)] transition-transform duration-500 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)" }}
      >
        <div className="flex items-center justify-between h-[74px] px-6 border-b border-[#E2E0D8]">
          <LangSwitch />
          <button
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            className="w-10 h-10 -mr-2 flex items-center justify-center text-[#141414]"
          >
            <X size={22} strokeWidth={1.4} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-6 pt-4">
          {nav.map((item, i) => (
            <div key={item.path} className="border-b border-[#E2E0D8]">
              <div className="flex items-baseline gap-4 py-4">
                <span className="mono text-[10px] text-[#A8A29A]">{String(i + 1).padStart(2, "0")}</span>
                <Link
                  to={item.path}
                  onClick={() => setOpen(false)}
                  className="display text-[28px] sm:text-[32px] text-[#141414] flex-1"
                >
                  {loc(item.label)}
                </Link>
                {item.kids.length > 0 && (
                  <button
                    type="button"
                    aria-label="Alt menü"
                    data-testid={`nav-mobile-toggle-${navId(item.path)}`}
                    onClick={() => setOpenSub((v) => (v === item.path ? "" : item.path))}
                    className="p-2 text-[#6B675F]"
                  >
                    <ChevronDown
                      size={18}
                      className={`transition-transform ${openSub === item.path ? "rotate-180" : ""}`}
                    />
                  </button>
                )}
              </div>
              {item.kids.length > 0 && openSub === item.path && (
                <div className="pb-4 pl-8 space-y-3">
                  {item.kids.map((kid) => (
                    <Link
                      key={kid.path}
                      to={kid.path}
                      onClick={() => setOpen(false)}
                      className="block text-[15px] text-[#141414]/75"
                    >
                      {loc(kid.label)}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="px-6 pb-8 pt-6 border-t border-[#E2E0D8]">
          <Link
            to="/contact"
            onClick={() => setOpen(false)}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#141414] text-[#FAFAFA] px-6 py-4 mono text-[10px]"
          >
            {loc(content.menu?.ctaLabel) || t("cta.quote")} <ArrowUpRight size={14} strokeWidth={1.6} />
          </Link>
          <p className="mono text-[8.5px] text-[#6B675F] mt-6 leading-relaxed">{loc(BRAND_L.meta)}</p>
        </div>
      </aside>
    </>
  );
}
